import { ArrowLeft, Check, ImagePlus, LoaderCircle, LogOut, Pencil, Plus, Tag, Trash2, UploadCloud, X } from "lucide-react";
import { type DragEvent, type FormEvent, type KeyboardEvent, useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { prepareImage, type PreparedImage } from "../lib/images";
import type { AdminSession, GalleryItem, GalleryPage, TagList } from "../shared";
import { ConfirmDeleteModal } from "./ConfirmDeleteModal";

interface EditorState {
  item: GalleryItem | null;
  title: string;
  prompt: string;
  tags: string[];
}

function sameTag(left: string, right: string) {
  return left.localeCompare(right, "en", { sensitivity: "accent" }) === 0;
}

function TagPicker({ selected, available, onChange }: { selected: string[]; available: string[]; onChange: (tags: string[]) => void }) {
  const [custom, setCustom] = useState("");

  const toggle = (tag: string) => {
    const current = selected.find((entry) => sameTag(entry, tag));
    if (current) onChange(selected.filter((entry) => entry !== current));
    else if (selected.length < 12) onChange([...selected, tag]);
  };

  const addCustom = () => {
    const tag = custom.trim().replace(/\s+/gu, " ");
    if (!tag || selected.some((entry) => sameTag(entry, tag)) || selected.length >= 12) return;
    const existing = available.find((entry) => sameTag(entry, tag));
    onChange([...selected, existing ?? tag]);
    setCustom("");
  };

  const customKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== "Enter") return;
    event.preventDefault();
    addCustom();
  };

  return (
    <section className="tag-field" aria-labelledby="tags-label">
      <div className="field-label" id="tags-label"><span>Tags</span><em>Optional · up to 12</em></div>
      <div className="custom-tag-row">
        <div className="custom-tag-input"><Tag size={16} /><input value={custom} onChange={(event) => setCustom(event.target.value)} onKeyDown={customKeyDown} maxLength={32} placeholder="Write a custom tag" /></div>
        <button type="button" onClick={addCustom} disabled={!custom.trim() || selected.length >= 12}>Add tag</button>
      </div>
      {available.length > 0 && (
        <div className="tag-options" aria-label="Existing tags">
          {available.map((tag) => {
            const active = selected.some((entry) => sameTag(entry, tag));
            return <button key={tag} type="button" className={`tag-choice ${active ? "is-selected" : ""}`} aria-pressed={active} disabled={!active && selected.length >= 12} onClick={() => toggle(tag)}>{active && <Check size={13} />}{tag}</button>;
          })}
        </div>
      )}
      {selected.length > 0 && (
        <div className="selected-tags" aria-label="Selected tags">
          {selected.map((tag) => <button key={tag} type="button" onClick={() => toggle(tag)} aria-label={`Remove ${tag}`}><span>{tag}</span><X size={13} /></button>)}
        </div>
      )}
    </section>
  );
}

function Editor({ state, availableTags, onCancel, onSaved }: { state: EditorState; availableTags: string[]; onCancel: () => void; onSaved: (item: GalleryItem) => void }) {
  const [title, setTitle] = useState(state.title);
  const [prompt, setPrompt] = useState(state.prompt);
  const [tags, setTags] = useState(state.tags);
  const [prepared, setPrepared] = useState<PreparedImage | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const input = useRef<HTMLInputElement>(null);

  useEffect(() => () => {
    if (prepared) URL.revokeObjectURL(prepared.previewUrl);
  }, [prepared]);

  const choose = async (file?: File) => {
    if (!file) return;
    setError(null);
    try {
      const next = await prepareImage(file);
      setPrepared((current) => {
        if (current) URL.revokeObjectURL(current.previewUrl);
        return next;
      });
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not prepare that image.");
    }
  };

  const onDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    void choose(event.dataTransfer.files[0]);
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!state.item && !prepared) {
      setError("Choose an image to publish.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const form = new FormData();
      form.set("title", title);
      form.set("prompt", prompt);
      form.set("tags", JSON.stringify(tags));
      if (prepared) {
        form.set("image", prepared.file);
        form.set("thumbnail", prepared.thumbnail);
        form.set("width", String(prepared.width));
        form.set("height", String(prepared.height));
      }
      const path = state.item ? `/api/admin/items/${state.item.id}` : "/api/admin/items";
      const saved = await api<GalleryItem>(path, { method: state.item ? "PUT" : "POST", body: form });
      onSaved(saved);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not save this work.");
    } finally {
      setBusy(false);
    }
  };

  const preview = prepared?.previewUrl || state.item?.thumbnailUrl;
  return (
    <form className="editor-panel" onSubmit={submit}>
      <div className="editor-heading">
        <div>
          <p className="eyebrow">{state.item ? "Edit archive entry" : "New archive entry"}</p>
          <h2>{state.item ? "Refine this work" : "Publish something new"}</h2>
        </div>
        {state.item && <button type="button" className="icon-button" onClick={onCancel} aria-label="Close editor"><X size={20} /></button>}
      </div>
      <div className="editor-grid">
        <div
          className={`drop-zone ${preview ? "has-image" : ""}`}
          onDragOver={(event) => event.preventDefault()}
          onDrop={onDrop}
          onClick={() => input.current?.click()}
          role="button"
          tabIndex={0}
          onKeyDown={(event) => (event.key === "Enter" || event.key === " ") && input.current?.click()}
        >
          <input ref={input} hidden type="file" accept="image/jpeg,image/png,image/webp" onChange={(event) => void choose(event.target.files?.[0])} />
          {preview ? <img src={preview} alt="Upload preview" /> : <div><UploadCloud size={28} /><strong>Drop an image here</strong><span>or choose a JPG, PNG or WebP · up to 20 MB</span></div>}
          {preview && <span className="replace-badge"><ImagePlus size={15} /> Replace image</span>}
        </div>
        <div className="editor-fields">
          <label>
            <span>Title <em>Optional</em></span>
            <input value={title} onChange={(event) => setTitle(event.target.value)} maxLength={180} placeholder="Give the image a short name" />
          </label>
          <label className="prompt-field">
            <span>Prompt <em>Optional</em></span>
            <textarea value={prompt} onChange={(event) => setPrompt(event.target.value)} maxLength={50_000} placeholder="Paste the full prompt behind this image" />
          </label>
          <TagPicker selected={tags} available={availableTags} onChange={setTags} />
          {error && <p className="form-error" role="alert">{error}</p>}
          <div className="form-actions">
            {state.item && <button type="button" className="button secondary" onClick={onCancel}>Cancel</button>}
            <button className="button primary" disabled={busy} type="submit">
              {busy ? <LoaderCircle size={17} className="spin" /> : state.item ? <Pencil size={17} /> : <Plus size={17} />}
              {busy ? "Saving…" : state.item ? "Save changes" : "Publish work"}
            </button>
          </div>
        </div>
      </div>
    </form>
  );
}

function Login({ onAuthenticated }: { onAuthenticated: () => void }) {
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api<AdminSession>("/api/admin/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password }),
      });
      onAuthenticated();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not sign in.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <main className="login-page">
      <a className="back-link" href="/"><ArrowLeft size={16} /> Back to the archive</a>
      <form className="login-card" onSubmit={submit}>
        <p className="brand-mark">NR</p>
        <p className="eyebrow">Private studio</p>
        <h1>Welcome back.</h1>
        <p>Enter the administrator password to manage Nana Republic.</p>
        <label>
          <span>Password</span>
          <input autoFocus type="password" autoComplete="current-password" value={password} onChange={(event) => setPassword(event.target.value)} required />
        </label>
        {error && <p className="form-error" role="alert">{error}</p>}
        <button className="button primary full-button" disabled={busy} type="submit">
          {busy && <LoaderCircle size={17} className="spin" />}{busy ? "Signing in…" : "Enter the studio"}
        </button>
      </form>
    </main>
  );
}

export function Admin() {
  const [authenticated, setAuthenticated] = useState<boolean | null>(null);
  const [items, setItems] = useState<GalleryItem[]>([]);
  const [availableTags, setAvailableTags] = useState<string[]>([]);
  const [editor, setEditor] = useState<EditorState | null>(null);
  const [editorVersion, setEditorVersion] = useState(0);
  const [notice, setNotice] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [deleteTarget, setDeleteTarget] = useState<GalleryItem | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const loadItems = async () => {
    setLoading(true);
    try {
      const [page, tagList] = await Promise.all([
        api<GalleryPage>("/api/admin/items?limit=48"),
        api<TagList>("/api/admin/tags"),
      ]);
      setItems(page.items);
      setAvailableTags(tagList.tags);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    api<AdminSession>("/api/admin/session")
      .then((session) => {
        setAuthenticated(session.authenticated);
        if (session.authenticated) return loadItems();
      })
      .catch(() => setAuthenticated(false));
  }, []);

  const signedIn = () => {
    setAuthenticated(true);
    void loadItems();
  };

  const saved = (item: GalleryItem) => {
    setItems((current) => {
      const exists = current.some((entry) => entry.id === item.id);
      return exists ? current.map((entry) => (entry.id === item.id ? item : entry)) : [item, ...current];
    });
    setEditor(null);
    setAvailableTags((current) => [...new Set([...current, ...item.tags])].sort((left, right) => left.localeCompare(right)));
    api<TagList>("/api/admin/tags").then((tagList) => setAvailableTags(tagList.tags)).catch(() => undefined);
    setEditorVersion((version) => version + 1);
    setNotice("The archive has been updated.");
    window.setTimeout(() => setNotice(null), 2500);
  };

  const remove = async (item: GalleryItem) => {
    setDeleteBusy(true);
    setDeleteError(null);
    try {
      await api<void>(`/api/admin/items/${item.id}`, { method: "DELETE" });
      setItems((current) => current.filter((entry) => entry.id !== item.id));
      if (editor?.item?.id === item.id) setEditor(null);
      setDeleteTarget(null);
      api<TagList>("/api/admin/tags").then((tagList) => setAvailableTags(tagList.tags)).catch(() => undefined);
      setNotice("The work was permanently deleted.");
      window.setTimeout(() => setNotice(null), 2500);
    } catch (caught) {
      setDeleteError(caught instanceof Error ? caught.message : "Could not delete this work.");
    } finally {
      setDeleteBusy(false);
    }
  };

  const requestDelete = (item: GalleryItem) => {
    setDeleteError(null);
    setDeleteTarget(item);
  };

  const cancelDelete = () => {
    if (deleteBusy) return;
    setDeleteTarget(null);
    setDeleteError(null);
  };

  const logout = async () => {
    await api<AdminSession>("/api/admin/logout", { method: "POST" });
    setAuthenticated(false);
    setItems([]);
  };

  if (authenticated === null) return <div className="center-loader"><LoaderCircle size={22} className="spin" /> Opening the studio</div>;
  if (!authenticated) return <Login onAuthenticated={signedIn} />;

  return (
    <main className="admin-page page-shell">
      <header className="admin-header">
        <div><a className="brand" href="/">Nana Republic</a><span>Private studio</span></div>
        <nav><a className="text-link" href="/"><ArrowLeft size={16} /> View archive</a><button className="text-link" type="button" onClick={() => void logout()}><LogOut size={16} /> Sign out</button></nav>
      </header>
      {notice && <div className="toast" role="status">{notice}</div>}
      <Editor
        key={`${editor?.item?.id || "new"}:${editorVersion}`}
        state={editor || { item: null, title: "", prompt: "", tags: [] }}
        availableTags={availableTags}
        onCancel={() => setEditor(null)}
        onSaved={saved}
      />
      <section className="admin-library">
        <div className="section-heading"><span>Published works</span><span>{items.length} total</span></div>
        {loading ? <div className="center-loader"><LoaderCircle size={20} className="spin" /> Loading works</div> : items.length === 0 ? (
          <div className="admin-empty"><p>Your archive is empty.</p><span>Publish the first image above.</span></div>
        ) : (
          <div className="admin-list">
            {items.map((item) => (
              <article key={item.id} className="admin-row">
                <img src={item.thumbnailUrl} alt="" />
                <div><h3>{item.title || "Untitled"}</h3><p>{item.prompt || "No prompt added"}</p>{item.tags.length > 0 && <div className="admin-row-tags">{item.tags.map((tag) => <span key={tag}>{tag}</span>)}</div>}</div>
                <div className="row-actions">
                  <button type="button" onClick={() => { setEditor({ item, title: item.title || "", prompt: item.prompt || "", tags: item.tags }); window.scrollTo({ top: 0, behavior: "smooth" }); }}><Pencil size={16} /> Edit</button>
                  <button type="button" className="danger" onClick={() => requestDelete(item)}><Trash2 size={16} /> Delete</button>
                </div>
              </article>
            ))}
          </div>
        )}
      </section>
      {deleteTarget && (
        <ConfirmDeleteModal
          title={deleteTarget.title || "Untitled"}
          busy={deleteBusy}
          error={deleteError}
          onCancel={cancelDelete}
          onConfirm={() => void remove(deleteTarget)}
        />
      )}
    </main>
  );
}
